import type { Color } from "../../common/color.js";
import { rectDeflate, type Insets, type Rect } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { Panel, type PanelOptions } from "./Panel.js";

export interface GroupBoxOptions extends PanelOptions {
  text?: string;
  font?: FontSpec | null;
  /** null uses the theme's subtle text color. */
  captionColor?: Color | null;
}

/** Gap either side of the caption where the frame is left out. */
const CAPTION_GAP = 6;
/** How far in from the left edge the caption starts. */
const CAPTION_INSET = 8;

/**
 * A titled frame round a group of related controls — WinForms' GroupBox.
 *
 * A `Panel` already groups; this adds the caption, and the notch in the top
 * border that the caption sits in. Radio buttons care: they are mutually
 * exclusive per container, so putting each set in its own GroupBox is how two
 * sets of options on one page stay independent of each other.
 */
export class GroupBox extends Panel {
  #text: string;

  font: FontSpec | null;
  captionColor: Color | null;

  constructor(options: GroupBoxOptions = {}) {
    super({ width: 200, height: 120, style: "none", ...options });
    this.#text = options.text ?? "";
    this.font = options.font ?? null;
    this.captionColor = options.captionColor ?? null;
  }

  get text(): string {
    return this.#text;
  }
  set text(v: string) {
    if (this.#text === v) return;
    this.#text = v;
    this.invalidate();
  }

  /** Height taken by the caption row, which the frame starts below. */
  captionHeight(theme: Theme): number {
    if (!this.#text) return 0;
    return Math.ceil(theme.metrics.font.size * 1.3);
  }

  /**
   * Inside the frame, past the caption. Children are positioned against this,
   * so a control at (0, 0) sits just inside the top-left of the box rather
   * than under its caption.
   */
  override get clientRect(): Rect {
    const theme = this.desktop?.theme;
    const top = theme ? this.captionHeight(theme) / 2 : 0;
    const inner: Insets = { left: 1, top: 1, right: 1, bottom: 1 };
    const frame = rectDeflate(
      { x: 0, y: top, width: this.width, height: Math.max(0, this.height - top) },
      inner,
    );
    return rectDeflate(frame, this.padding);
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    const font = this.font ?? theme.metrics.font;
    const caption = this.captionHeight(theme);
    const top = caption / 2;
    const frame: Rect = {
      x: 0.5,
      y: top,
      width: Math.max(0, this.width - 1),
      height: Math.max(0, this.height - top),
    };

    if (this.background) rc.fillRect(frame, this.background, theme.metrics.controlCornerRadius);
    rc.strokeRect(frame, p.controlBorder, 1, theme.metrics.controlCornerRadius);

    if (!this.#text) return;
    // Punch a gap in the top border and put the caption in it, rather than
    // drawing the text over the line and leaving it struck through.
    const width = rc.measureText(this.#text, font).width;
    const gap = Math.min(width + CAPTION_GAP * 2, Math.max(0, this.width - CAPTION_INSET * 2));
    rc.fillRect(
      { x: CAPTION_INSET, y: top - 1, width: gap, height: 2 },
      this.background ?? p.windowBg,
    );
    rc.drawText(
      this.#text,
      CAPTION_INSET + CAPTION_GAP,
      0,
      font,
      this.captionColor ?? (this.effectivelyEnabled ? p.textSubtle : p.textDisabled),
      { baseline: "top", maxWidth: Math.max(0, gap - CAPTION_GAP * 2) },
    );
  }
}
